import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import './CustomTooltip.css'

/**
 * CustomTooltip - A reusable tooltip component
 *
 * @param {string} content - Tooltip content (text or React node)
 * @param {ReactNode} children - Element to attach tooltip to
 * @param {string} preferredPosition - Position: 'above', 'below', 'left', 'right' (default: 'above')
 * @param {boolean} disabled - Disable tooltip (default: false)
 * @param {number} delay - Delay before showing tooltip in ms (default: 0)
 * @param {boolean} renderInPortal - Render tooltip in document.body so it is not clipped by overflow/stacking (e.g. inside tables)
 * @param {string} className - Additional CSS class for wrapper
 * @param {string} tooltipClassName - Additional CSS class for tooltip
 * @param {object} style - Additional inline styles for wrapper
 * @param {object} tooltipStyle - Additional inline styles for tooltip
 * @param {number} maxWidth - Maximum width of tooltip in px (default: 300)
 * @param {number} minWidth - Minimum width of tooltip in px (default: 0, fit content)
 * @param {number} zIndex - Z-index for tooltip (default: undefined, uses CSS default)
 * @param {number} spacing - Gap in px between tooltip and trigger (default: 8)
 */
const CustomTooltip = ({
  content,
  children,
  preferredPosition = 'above',
  disabled = false,
  delay = 0,
  renderInPortal = false,
  className = '',
  tooltipClassName = '',
  style = {},
  tooltipStyle = {},
  maxWidth = 300,
  minWidth = 0,
  zIndex,
  spacing = 8,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [actualPosition, setActualPosition] = useState(preferredPosition)
  const [portalPosition, setPortalPosition] = useState(null)
  const [portalVisible, setPortalVisible] = useState(false)
  const timeoutRef = useRef(null)
  const wrapperRef = useRef(null)
  const tooltipRef = useRef(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isVisible || !wrapperRef.current) {
      if (!isVisible && renderInPortal) {
        setPortalPosition(null)
        setPortalVisible(false)
      }
      return
    }

    if (renderInPortal) {
      setPortalVisible(false)
      const runMeasure = () => {
        if (!wrapperRef.current || !tooltipRef.current) return
        const wr = wrapperRef.current.getBoundingClientRect()
        const tr = tooltipRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight

        let position = preferredPosition
        if (preferredPosition === 'above' && wr.top - tr.height - spacing < 0) {
          position = 'below'
        } else if (preferredPosition === 'below' && wr.bottom + tr.height + spacing > viewportHeight) {
          position = 'above'
        } else if (preferredPosition === 'left' && wr.left - tr.width - spacing < 0) {
          position = 'right'
        } else if (preferredPosition === 'right' && wr.right + tr.width + spacing > viewportWidth) {
          position = 'left'
        }

        setActualPosition(position)

        let top = 0
        let left = 0
        if (position === 'above') {
          top = wr.top - tr.height - spacing
          left = wr.left + wr.width / 2 - tr.width / 2
        } else if (position === 'below') {
          top = wr.bottom + spacing
          left = wr.left + wr.width / 2 - tr.width / 2
        } else if (position === 'left') {
          top = wr.top + wr.height / 2 - tr.height / 2
          left = wr.left - tr.width - spacing
        } else {
          top = wr.top + wr.height / 2 - tr.height / 2
          left = wr.right + spacing
        }
        setPortalPosition({ top: Math.round(top), left: Math.round(left) })
        setPortalVisible(true)
      }
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(runMeasure)
      })
      return () => cancelAnimationFrame(rafId)
    } else {
      if (!tooltipRef.current) return
      const wrapperRect = wrapperRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let position = preferredPosition
      if (preferredPosition === 'above' && wrapperRect.top - tooltipRect.height - spacing < 0) {
        position = 'below'
      } else if (preferredPosition === 'below' && wrapperRect.bottom + tooltipRect.height + spacing > viewportHeight) {
        position = 'above'
      } else if (preferredPosition === 'left' && wrapperRect.left - tooltipRect.width - spacing < 0) {
        position = 'right'
      } else if (preferredPosition === 'right' && wrapperRect.right + tooltipRect.width + spacing > viewportWidth) {
        position = 'left'
      }
      setActualPosition(position)
      setPortalPosition(null)
    }
  }, [isVisible, preferredPosition, renderInPortal, spacing])

  const handleMouseEnter = () => {
    if (disabled || !content) return

    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true)
      }, delay)
    } else {
      setIsVisible(true)
    }
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsVisible(false)
  }

  const wrapperClasses = `CustomTooltip__wrapper ${className}`.trim()
  const tooltipClasses = `CustomTooltip CustomTooltip--${actualPosition} ${tooltipClassName} ${renderInPortal && portalPosition ? 'CustomTooltip--portal' : ''}`.trim()

  const baseTooltipStyle = {
    maxWidth: maxWidth === 'none' ? 'none' : `${maxWidth}px`,
    ...(minWidth > 0 && { minWidth: `${minWidth}px` }),
    width: 'auto',
    ...(zIndex !== undefined && { zIndex }),
    ...tooltipStyle,
  }

  const innerWhiteSpace = tooltipStyle.whiteSpace ?? 'pre-line'

  const tooltipEl = isVisible && content && (
    <div
      ref={tooltipRef}
      className={tooltipClasses}
      data-position={actualPosition}
      style={
        renderInPortal
          ? (portalPosition && portalVisible
              ? { ...baseTooltipStyle, position: 'fixed', top: portalPosition.top, left: portalPosition.left, transform: 'none', bottom: 'auto', right: 'auto', visibility: 'visible' }
              : { ...baseTooltipStyle, position: 'fixed', top: 0, left: 0, transform: 'none', bottom: 'auto', right: 'auto', visibility: 'hidden' })
          : baseTooltipStyle
      }
    >
      {typeof content === 'string' ? (
        <span style={{ whiteSpace: innerWhiteSpace }}>{content}</span>
      ) : (
        content
      )}
    </div>
  )

  return (
    <div
      ref={wrapperRef}
      className={wrapperClasses}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={style}
    >
      {children}
      {renderInPortal ? (tooltipEl && createPortal(tooltipEl, document.body)) : tooltipEl}
    </div>
  )
}

export default CustomTooltip
